import * as Pipeline from '../core/utils/pipeline';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Component, Element, Event, EventEmitter, Prop, State, VNode, Watch, h } from '@stencil/core';
import { ExpansionChangeEvent, ExpansionChangeHandler } from './ads-expansion-panel.types';
import { Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { generateSVGImage, isNullOrUndefined } from 'utils/index';
import { height as iconDownHeight, svgPathData as iconDownPathData, width as iconDownWidth } from '@fortawesome/pro-regular-svg-icons/faChevronDown';

/**
 * The Expansion Panel component enables large amounts of content to be displayed in a small space through progressive disclosure.
 * The header title describes the category of the content enabling the user to decide which sections are relevant for them.
 * @tag `<ads-expansion-panel>`
 * @slot toggle - Content rendered as rich text inside the component toggle
 * @slot content - Content rendered as rich text inside the component panel
 * @example <ads-expansion-panel>Hello world</ads-expansion-panel>
 */
@Component({
  tag: 'ads-expansion-panel',
  styleUrl: 'ads-expansion-panel.scss',
  shadow: true,
})
export class AdsExpansionPanel implements BaseComponent {
  /**
   * @hidden
   */
  component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-expansion-panel'));
    this.component = 'ads-expansion-panel';
  }

  /**
   * The component element
   * @hidden
   */
  @Element() el: HTMLAdsExpansionPanelElement;

  /**
   * @hidden
   */
  @State() internalExpanded: boolean;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * Whether or not the panel is expanded (optional)
   */
  @Prop({ reflect: true, attribute: 'expanded' }) expanded?: boolean;

  /**
   * Whether or not the panel is expanded on first render (optional)
   * @default false
   */
  @Prop({ reflect: true }) isDefaultExpanded = false;

  /**
   * Callback function triggered on expansion change (optional)
   */
  @Prop() expansionChangeHandler?: ExpansionChangeHandler;

  /**
   * Emits the 'expansionChange' custom event.<br/>
   * Usage: `document.querySelector('ads-select').addEventListener('expansionChange', function(evt) {});`
   * @event
   */
  @Event({ bubbles: false }) expansionChange: EventEmitter<ExpansionChangeEvent>;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-expansion-panel'));
  }

  /**
   * Watch for expanded prop change
   * @param {boolean}   newExpanded  The new value for 'expanded'
   * @private
   * @hidden
   */
  @Watch('expanded')
  onValueChange(newExpanded: boolean): void {
    if (newExpanded !== undefined && newExpanded !== null) {
      this.setInternalExpanded(this.expanded);
    }
  }

  /**
   * Sets the internal state to the given value
   * @param   {boolean}  newValue  The new selected option value
   * @returns {void}
   */
  private setInternalExpanded = (newValue: boolean): void => {
    this.internalExpanded = newValue;
    this.updateContentPanel();
  };

  /**
   * Update the classes on the content panel
   * @returns {void}
   */
  private updateContentPanel(): void {
    if (this.internalExpanded) {
      this.el.shadowRoot.querySelector('.c-expansion-panel').classList.add('is-expanded');
    } else {
      this.el.shadowRoot.querySelector('.c-expansion-panel').classList.remove('is-expanded');
    }
  }

  /**
   * Handle click events
   * @param   {Event}   evt   The original event
   * @returns {void}
   */
  private onClickHandler = (evt: Event): void => {
    this.expansionChange.emit({ evt, expanded: this.internalExpanded });
    if (!isNullOrUndefined(this.expansionChangeHandler)) {
      this.expansionChangeHandler({ evt, expanded: this.internalExpanded });
    }
    if (this.expanded === undefined || this.expanded === null) {
      this.setInternalExpanded(!this.internalExpanded);
    }
  };

  /**
   * Handle key down events
   * @param {KeyboardEvent}   evt     The keyboard event
   * @returns {void}
   */
  private onKeyDownHandler = (evt: KeyboardEvent): void => {
    if (evt.code !== 'Tab') {
      evt.preventDefault();
    }
    if (evt.code !== 'Enter' && evt.code !== 'Space') {
      return;
    }
    this.expansionChange.emit({ evt, expanded: this.internalExpanded });
    if (!isNullOrUndefined(this.expansionChangeHandler)) {
      this.expansionChangeHandler({ evt, expanded: this.internalExpanded });
    }
    if (this.expanded === undefined || this.expanded === null) {
      this.setInternalExpanded(!this.internalExpanded);
    }
  };

  protected componentWillLoad(): void {
    this.internalExpanded =
      this.expanded !== undefined && this.expanded !== null
        ? this.expanded
        : this.isDefaultExpanded !== undefined && this.isDefaultExpanded !== null
        ? this.isDefaultExpanded
        : false;
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
    this.updateContentPanel();
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    const toggleAttributes = {
      role: 'button',
      'aria-controls': `ads:expansion-panel:${this.identifier}:content`,
      'aria-expanded': this.internalExpanded.toString(),
      tabIndex: 0,
    };

    const contentAttributes = {
      'aria-hidden': (!this.internalExpanded).toString(),
    };

    const defaultIconDownPathData =
      'M239 401c9.4 9.4 24.6 9.4 33.9 0L465 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-175 175L81 175c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L239 401z';
    const defaultIconDownHeight = 512;
    const defaultIconDownWidth = 512;

    const $icon = {
      data: iconDownPathData || defaultIconDownPathData,
      height: iconDownHeight || defaultIconDownHeight,
      width: iconDownWidth || defaultIconDownWidth,
    };

    return (
      <div class="c-expansion-panel">
        <div class="c-expansion-panel__toggle" {...toggleAttributes} onClick={this.onClickHandler} onKeyDown={this.onKeyDownHandler}>
          <slot name="toggle" />
          <span class="c-expansion-panel__toggle__icon" role="presentation">
            {generateSVGImage($icon)}
          </span>
        </div>
        {this.internalExpanded && <hr class="c-expansion-panel__hr"></hr>}
        <div class="c-expansion-panel__content" id={`ads:expansion-panel:${this.identifier}:content`} {...contentAttributes}>
          <slot name="content" />
        </div>
      </div>
    ) as VNode;
  }
}
